/**
 * PRD.252 — Payment-first entry ("+ New Payment").
 *
 * Lets front-desk staff record a payment without opening an invoice first. The
 * allocation area lists ALL of the patient's outstanding invoice items (across
 * unpaid invoices), with the invoice reference on each row. The entered amount
 * is auto-allocated oldest-invoice-first (§5.9): exactly one outstanding item →
 * auto-assigned in full; multiple → auto-split. The result is shown and fully
 * editable before save (§5.10/§5.11).
 *
 * On save the payment is split into one `invoice_payments` row PER invoice
 * (each carries that invoice's item allocations) so per-invoice balances and
 * statuses stay correct — exactly like invoice-scoped Add Payment, just done
 * for several invoices at once. Any amount beyond the outstanding total is
 * recorded as a patient-level payment.
 *
 * This is additive: invoice-scoped Add Payment (RecordPaymentDialog) is
 * unchanged and still limited to its one invoice.
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qPatientInvoicesWithPaid,
  qInvoiceItemsByPatient,
  qPaymentsByPatient,
  aggregatePaymentAllocations,
  type PatientInvoiceRow,
  type PatientInvoiceItemRow,
  type InvoicePaymentRow,
} from '@helios/data-access';
import { isPositive, parseAmountInput, phpFromCents, toCents } from '@helios/domain';
import { useFormatMoney, useRegionalSettings } from '../../hooks/useFormatMoney';
import { itemChargeCents, inputToCents } from './payment-allocation-utils';
import { syncPatientPackagePaymentStatusFromInvoice } from '../../services/patient-package-service';
import { recomputeInvoiceItemPaymentStatus } from '../../services/invoice-item-settlement';

type PaymentMethod = 'cash' | 'card' | 'bank_transfer' | 'gcash' | 'other';

const METHOD_OPTIONS: ReadonlyArray<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'gcash', label: 'GCash' },
  { value: 'other', label: 'Other' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  orgId: string;
  onRecorded?: () => void;
}

/** One allocatable outstanding line (item-level, or whole-invoice fallback). */
interface OutItem {
  invoiceId: string;
  invoiceNo: string | null;
  dateIso: string | null;
  itemId: string | null; // null = invoice-level (no item breakdown)
  itemName: string;
  outstandingCents: number;
}

function rowKey(r: OutItem): string {
  return `${r.invoiceId}:${r.itemId ?? 'inv'}`;
}

function todayISODate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function visitDateToIso(date: string): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

/** Largest-remainder split of `total` across weights so parts sum to total. */
function distributeProRata(total: number, weights: number[]): number[] {
  const totalWeight = weights.reduce((s, w) => s + w, 0);
  if (total <= 0 || totalWeight <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (total * w) / totalWeight);
  const floors = exact.map((e) => Math.floor(e));
  let leftover = total - floors.reduce((s, f) => s + f, 0);
  const order = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floors];
  for (let k = 0; k < order.length && leftover > 0; k++, leftover--) out[order[k].i]++;
  return out;
}

export function NewPaymentDialog({ open, onOpenChange, patientId, orgId, onRecorded }: Props) {
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;

  const [amountInput, setAmountInput] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [notes, setNotes] = useState('');
  const [visitDate, setVisitDate] = useState<string>(() => todayISODate());
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [manual, setManual] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = open && !!patientId;
  const noop = { key: ['noop'] as const, sql: 'SELECT NULL WHERE 0', params: [], watch: [] };
  const { data: invoices = [] } = usePowerSyncQuery<PatientInvoiceRow>(
    enabled ? qPatientInvoicesWithPaid(patientId) : noop,
  );
  const { data: items = [] } = usePowerSyncQuery<PatientInvoiceItemRow>(
    enabled ? qInvoiceItemsByPatient(patientId) : noop,
  );
  const { data: payments = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    enabled ? qPaymentsByPatient(patientId) : noop,
  );

  // Outstanding items across all unpaid invoices, oldest invoice first.
  const outItems = useMemo<OutItem[]>(() => {
    const knownIds = new Set(items.map((i) => i.id));
    const { allocatedByItem } = aggregatePaymentAllocations(payments, knownIds);
    const itemsByInvoice = new Map<string, PatientInvoiceItemRow[]>();
    for (const it of items) {
      if (!it.invoice_id) continue;
      const list = itemsByInvoice.get(it.invoice_id);
      if (list) list.push(it);
      else itemsByInvoice.set(it.invoice_id, [it]);
    }
    const openInvoices = invoices
      .filter((inv) => inv.status !== 'draft' && inv.balance_cents > 0)
      .sort((a, b) => ((a.date_iso ?? '') < (b.date_iso ?? '') ? -1 : 1));

    const out: OutItem[] = [];
    for (const inv of openInvoices) {
      const its = itemsByInvoice.get(inv.id) ?? [];
      const raw = its.map((it) =>
        Math.max(0, itemChargeCents(it) - (allocatedByItem.get(it.id) ?? 0)),
      );
      const rawSum = raw.reduce((s, v) => s + v, 0);
      // Never offer more than the invoice actually owes (caps unallocated cases).
      const caps = rawSum > inv.balance_cents ? distributeProRata(inv.balance_cents, raw) : raw;
      let added = 0;
      its.forEach((it, i) => {
        if (caps[i] > 0) {
          out.push({
            invoiceId: inv.id,
            invoiceNo: inv.invoice_no,
            dateIso: inv.date_iso,
            itemId: it.id,
            itemName: it.service_name || it.description_snapshot || it.free_text_name || 'Item',
            outstandingCents: caps[i],
          });
          added += caps[i];
        }
      });
      // Fallback: invoice owes but has no item breakdown to allocate against.
      if (added < inv.balance_cents) {
        out.push({
          invoiceId: inv.id,
          invoiceNo: inv.invoice_no,
          dateIso: inv.date_iso,
          itemId: null,
          itemName: 'Invoice balance',
          outstandingCents: inv.balance_cents - added,
        });
      }
    }
    return out;
  }, [invoices, items, payments]);

  const totalOutstandingCents = useMemo(
    () => outItems.reduce((s, r) => s + r.outstandingCents, 0),
    [outItems],
  );

  // Allocate `amountCents` oldest-first across the outstanding items.
  function autoAllocate(amountCents: number): Record<string, string> {
    let remaining = amountCents;
    const next: Record<string, string> = {};
    for (const r of outItems) {
      if (remaining <= 0) break;
      const give = Math.min(r.outstandingCents, remaining);
      if (give > 0) next[rowKey(r)] = (give / 100).toFixed(2);
      remaining -= give;
    }
    return next;
  }

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setMethod('cash');
    setNotes('');
    setVisitDate(todayISODate());
    setSubmitting(false);
    setError(null);
    setManual(false);
    setAmountTouched(false);
    setAmountInput('');
  }, [open]);

  // Default the amount to the full outstanding total once the data loads
  // (unless the user has typed an amount) — the common "pay everything" case.
  useEffect(() => {
    if (!open || amountTouched) return;
    setAmountInput(totalOutstandingCents > 0 ? (totalOutstandingCents / 100).toFixed(2) : '');
  }, [open, amountTouched, totalOutstandingCents]);

  // Re-run auto-allocation when the amount changes (until the user edits
  // allocations manually — then they're in control).
  const parsed = useMemo(() => parseAmountInput(amountInput), [amountInput]);
  const amountCents = parsed ? toCents(parsed) : 0;
  useEffect(() => {
    if (!open || manual) return;
    setAlloc(autoAllocate(amountCents));
  }, [open, manual, amountCents, outItems]);

  const allocatedCents = useMemo(
    () => Object.values(alloc).reduce((s, v) => s + inputToCents(v), 0),
    [alloc],
  );
  const overAllocated = allocatedCents > amountCents;
  const unallocatedCents = Math.max(0, amountCents - allocatedCents);

  const setItemAlloc = (r: OutItem, value: string) => {
    setManual(true);
    setError(null);
    // Cap at the item's outstanding so a row can't be overpaid.
    const cents = inputToCents(value);
    const capped = Math.min(cents, r.outstandingCents);
    const str = value === '' ? '' : capped > 0 ? (capped / 100).toFixed(2) : '';
    setAlloc((prev) => ({ ...prev, [rowKey(r)]: str }));
  };

  const fillItem = (r: OutItem) => {
    setManual(true);
    setError(null);
    const otherAllocated = allocatedCents - inputToCents(alloc[rowKey(r)]);
    const available = Math.max(0, amountCents - otherAllocated);
    const give = Math.min(r.outstandingCents, available);
    setAlloc((prev) => ({ ...prev, [rowKey(r)]: give > 0 ? (give / 100).toFixed(2) : '' }));
  };

  const clearItem = (r: OutItem) => {
    setManual(true);
    setError(null);
    setAlloc((prev) => ({ ...prev, [rowKey(r)]: '' }));
  };

  const canSubmit = !!db && !!parsed && isPositive(parsed) && !overAllocated && !submitting;

  async function handleSubmit() {
    if (!db || !parsed) return;
    if (!isPositive(parsed)) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (overAllocated) {
      setError('Allocations exceed the payment amount.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const paidAt = visitDateToIso(visitDate);
      const updatedBy = user?.uid ?? null;

      // Group the per-item allocations by invoice → one payment row per invoice.
      type Group = {
        invoiceId: string;
        total: number;
        allocations: { item_id: string; amount_cents: number }[];
      };
      const groups = new Map<string, Group>();
      for (const r of outItems) {
        const cents = inputToCents(alloc[rowKey(r)]);
        if (cents <= 0) continue;
        const g = groups.get(r.invoiceId) ?? { invoiceId: r.invoiceId, total: 0, allocations: [] };
        g.total += cents;
        if (r.itemId) g.allocations.push({ item_id: r.itemId, amount_cents: cents });
        groups.set(r.invoiceId, g);
      }

      const invById = new Map(invoices.map((inv) => [inv.id, inv]));

      for (const g of groups.values()) {
        const inv = invById.get(g.invoiceId);
        await db.execute(
          `INSERT INTO invoice_payments
             (id, org_id, invoice_id, patient_id, amount_cents, method, notes, allocations, paid_at, created_at, updated_at, updated_by)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            g.invoiceId,
            patientId,
            g.total,
            method,
            notes.trim() || null,
            g.allocations.length ? JSON.stringify(g.allocations) : null,
            paidAt,
            now,
            now,
            updatedBy,
          ],
        );

        // Re-sync invoice status + treatment-record mirror (same as Add Payment).
        if (inv) {
          const newPaid = (inv.paid_cents ?? 0) + g.total;
          const fullySettled = inv.grand_cents > 0 && newPaid >= inv.grand_cents;
          const nextStatus = fullySettled
            ? 'paid'
            : inv.status === 'draft'
              ? 'issued'
              : (inv.status ?? 'issued');
          if (nextStatus !== inv.status) {
            await db.execute(`UPDATE invoices SET status = ?, updated_at = ? WHERE id = ?`, [
              nextStatus,
              now,
              g.invoiceId,
            ]);
          }
          const linked = await db.getAll<{ id: string; amount_charged_cents: number }>(
            `SELECT id, amount_charged_cents FROM patient_treatment_records WHERE invoice_id = ?`,
            [g.invoiceId],
          );
          for (const rec of linked) {
            await db.execute(
              `UPDATE patient_treatment_records SET amount_paid_cents = ?, updated_at = ? WHERE id = ?`,
              [fullySettled ? rec.amount_charged_cents : 0, now, rec.id],
            );
          }

          // PRD.261 — activate/refresh patient packages on this invoice.
          await syncPatientPackagePaymentStatusFromInvoice({ db, invoiceId: g.invoiceId });

          // Per-item settlement → per-item pharmacy dispense eligibility.
          await recomputeInvoiceItemPaymentStatus(db, g.invoiceId);
        }
      }

      // Any amount beyond outstanding → patient-level (unlinked) payment.
      if (unallocatedCents > 0) {
        await db.execute(
          `INSERT INTO invoice_payments
             (id, org_id, invoice_id, patient_id, amount_cents, method, notes, allocations, paid_at, created_at, updated_at, updated_by)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            null,
            patientId,
            unallocatedCents,
            method,
            notes.trim() || null,
            null,
            paidAt,
            now,
            now,
            updatedBy,
          ],
        );
      }

      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      console.error('[NewPaymentDialog] save failed', e);
      setError('Could not save payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Group outstanding rows by invoice for display.
  const grouped = useMemo(() => {
    const map = new Map<string, { inv: OutItem; rows: OutItem[] }>();
    for (const r of outItems) {
      const g = map.get(r.invoiceId);
      if (g) g.rows.push(r);
      else map.set(r.invoiceId, { inv: r, rows: [r] });
    }
    return [...map.values()];
  }, [outItems]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Record Payment"
      description="Apply a payment across the patient's outstanding invoices."
      maxWidth={560}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--sp-3)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Allocated {fmt(phpFromCents(allocatedCents))} of {fmt(phpFromCents(amountCents))}
            {unallocatedCents > 0 && ` · ${fmt(phpFromCents(unallocatedCents))} unallocated`}
          </span>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button
              variant="ghost"
              size="md"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button variant="gradient" size="md" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Saving…' : 'Record Payment'}
            </Button>
          </div>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5) var(--sp-6)',
        }}
      >
        {/* Payment fields */}
        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <Field label="Visit Date">
            <input
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value || todayISODate())}
              style={inputStyle()}
            />
          </Field>
          <Field label="Amount">
            <div style={{ position: 'relative' }}>
              <span style={amountPrefixStyle}>{currencySymbol}</span>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => {
                  setManual(false);
                  setAmountTouched(true);
                  setError(null);
                  setAmountInput(e.target.value);
                }}
                placeholder="0.00"
                style={{ ...inputStyle(), paddingLeft: 28 }}
              />
            </div>
          </Field>
          <Field label="Method">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              style={inputStyle()}
            >
              {METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Note (optional)">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. OR #1234"
            style={inputStyle()}
          />
        </Field>

        {/* Allocation */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Allocate to outstanding items
          </span>
          <button
            type="button"
            onClick={() => {
              setManual(false);
              setAlloc(autoAllocate(amountCents));
            }}
            style={{
              border: 'none',
              background: 'none',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Auto-allocate
          </button>
        </div>

        {outItems.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-5)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            No outstanding items — nothing to allocate. The payment will be recorded patient-level.
          </div>
        ) : (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {grouped.map((g) => (
              <div key={g.inv.invoiceId}>
                <div
                  style={{
                    padding: 'var(--sp-2) var(--sp-3)',
                    background: 'var(--bg-muted, #f9fafb)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  {g.inv.invoiceNo ?? g.inv.invoiceId.slice(0, 8)}
                  {g.inv.dateIso ? ` · ${g.inv.dateIso}` : ''}
                </div>
                {g.rows.map((r) => (
                  <div
                    key={rowKey(r)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                      padding: 'var(--sp-2) var(--sp-3)',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.itemName}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                        {fmt(phpFromCents(r.outstandingCents))} outstanding
                      </div>
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={alloc[rowKey(r)] ?? ''}
                      onChange={(e) => setItemAlloc(r, e.target.value)}
                      placeholder="0.00"
                      style={{ ...inputStyle(), width: 110, textAlign: 'right' }}
                    />
                    <button type="button" onClick={() => fillItem(r)} style={rowBtnStyle(true)}>
                      Full
                    </button>
                    <button
                      type="button"
                      onClick={() => clearItem(r)}
                      disabled={!alloc[rowKey(r)]}
                      style={rowBtnStyle(!!alloc[rowKey(r)])}
                    >
                      Clear
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div
            style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-danger-text, #dc2626)' }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Field + input helpers ───

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

/** Compact per-row action button (Full / Clear). Muted when disabled. */
function rowBtnStyle(active: boolean): React.CSSProperties {
  return {
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: active ? 'var(--color-primary)' : 'var(--text-placeholder)',
    borderRadius: 'var(--radius-sm)',
    height: 28,
    padding: '0 8px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: active ? 'pointer' : 'default',
    flexShrink: 0,
  };
}

function inputStyle(): React.CSSProperties {
  return {
    width: '100%',
    height: 'var(--input-height, 40px)',
    padding: '0 var(--sp-3)',
    borderRadius: 'var(--input-radius, 10px)',
    border: '1px solid var(--border-input)',
    background: 'var(--input-bg)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--input-text)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
}

const amountPrefixStyle: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-sm)',
  pointerEvents: 'none',
};
